import React, { FC, useEffect } from 'react'

import { useDispatch } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { useParams } from 'react-router-dom'

import { handleUniNoticeAction } from 'actions/uninotice/commonUniNoticeActions'
import { toggleInlineNoticeAction } from 'actions/uninotice/toggleInlineNoticeAction'
import {
  clearInlineNoticeAction,
  fetchInlineNoticePageAction,
  saveUniNoticeClickStatisticsAction,
} from 'actions/uninotice/uniNoticeAction'
import { UniNoticePayload } from 'api/uninotice/uninotice.types'
import { mambaLayoutContextId } from 'components/layout/MambaLayout/Context'
import { CreateBaseUrl } from 'components/layout/MambaLayout/CreateContext'
import { indexPath, UniNoticeInlineRouteParams } from 'components/paths'
import { DefaultLoader } from 'components/presentational/AnimatedWatch/AnimatedWatch'
import { CommonNoticeView } from 'components/uninotice/CommonNoticeView'
import { UniNoticeType } from 'components/uninotice/UniNotice.constants'
import { BoxNotice } from 'components/uninotice/UniNoticeView.styled'
import { replace } from 'functions/router'
import { useRedesign } from 'hooks/useRedesign'
import { useReducersInsert } from 'hooks/useReducersInsert'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'
import { uniNoticeReducer } from 'reducers/uniNoticeReducer'

import { CommonNoticeViewNext } from './CommonNoticeViewNext'
import { InlineNoticeTypes, UniNoticeId } from './uninotice.types'

const UniNoticeInlinePage: FC<{
  noticeId?: string
  viewWithoutBorder?: boolean
  /**
   * Из-за немедленности задачи вставляю костыль, причина ошибки:
   * неправильное использование компонента в промо стримов.
   **/
  notPage?: boolean
  /** Нужен для того, чтобы с помощью React.createPortal зарендерить кнопку юнинотиса в другом месте DOM */
  primaryButtonPortalContainer?: HTMLElement | null
}> = (props) => {
  useReducersInsert({ uniNoticeReducer: uniNoticeReducer })
  const params = useParams<UniNoticeInlineRouteParams>()
  const match = useRouteMatch()
  const dispatch = useDispatch()

  const noticeId = params.noticeId ?? props.noticeId
  const { viewWithoutBorder, primaryButtonPortalContainer } = props

  const { inlineNoticePage, noticeNotFound } = useShallowEqualSelector(
    ({ uniNoticeReducer: { inlineNoticePage, noticeNotFound } }) => ({
      inlineNoticePage,
      noticeNotFound,
    })
  )

  useEffect(() => {
    dispatch(toggleInlineNoticeAction(noticeId, true))
    dispatch(fetchInlineNoticePageAction(noticeId))

    return () => {
      dispatch(clearInlineNoticeAction())
    }
  }, [dispatch, noticeId])

  useEffect(() => {
    if (noticeNotFound) {
      dispatch(replace(indexPath, false, 'inline-not-found'))
    }
  }, [dispatch, noticeNotFound])

  const handlePrimaryClick = () => {
    if (inlineNoticePage) {
      const { payload: rawPayload, noticeId, type } = inlineNoticePage
      const payload = rawPayload as UniNoticePayload
      const { actionId, data } = payload.actionBtn.action
      const { display } = payload.appearance!

      dispatch(handleUniNoticeAction(actionId, noticeId, data!, type, display))
      dispatch(saveUniNoticeClickStatisticsAction(noticeId, actionId))
    }
  }

  const handleSecondaryClick = () => {
    if (inlineNoticePage) {
      const { payload: rawPayload, noticeId, type } = inlineNoticePage
      const payload = rawPayload as UniNoticePayload
      const { actionId, data } = payload.auxBtn.action
      const { display } = payload.appearance!

      dispatch(handleUniNoticeAction(actionId, noticeId, data!, type, display))
    }
  }

  const redesign = useRedesign()

  /**
   * Сматчили и нотис пришел с бека.
   */
  if (match && inlineNoticePage) {
    if (
      redesign &&
      // Редизайним серверные нотисы поштучно
      redesignedNoticesIdsList.includes(noticeId as UniNoticeId)
    ) {
      return (
        <CreateBaseUrl
          baseUrl={match.url}
          basePath={match.path}
          contextId={mambaLayoutContextId}
        >
          <CommonNoticeViewNext
            notice={inlineNoticePage}
            onActionClick={handlePrimaryClick}
            onSecondaryClick={handleSecondaryClick}
          />
        </CreateBaseUrl>
      )
    }

    const withNavigation = [UniNoticeType.user_ban_reasons].includes(
      noticeId as UniNoticeType
    )

    const element = (
      <BoxNotice>
        <CommonNoticeView
          notice={inlineNoticePage}
          viewWithoutBorder={viewWithoutBorder}
          primaryButtonPortalContainer={primaryButtonPortalContainer}
          onActionClick={handlePrimaryClick}
          onSecondaryClick={handleSecondaryClick}
          withNavigation={withNavigation}
        />
      </BoxNotice>
    )

    if (props.notPage) {
      return element
    }

    return (
      <CreateBaseUrl
        baseUrl={match.url}
        basePath={match.path}
        contextId={mambaLayoutContextId}
      >
        {element}
      </CreateBaseUrl>
    )
  }

  return <DefaultLoader />
}

export default UniNoticeInlinePage

export const redesignedNoticesIdsList = [
  UniNoticeId.password_recovery_success,
  InlineNoticeTypes.passwordRecoveryLinkExpired,
  UniNoticeId.userBanned,
  UniNoticeId.user_ban_reasons,
]
